import Point from './Point';
import Bounds from './Bounds';
import Transformation from './Transformation';
import * as Coordinates from './Coordinates';
import { Res } from 'core/api';

export const MapResizeModes = ['shrink', 'vertical-fit', 'fit'] as const;
export type MapResizeMode = typeof MapResizeModes[number];

export const FormatMapResizeMode = (mode: typeof MapResizeModes[number]): string => {
	switch (mode) {
		case 'shrink': return Res.Const.MAP_RESIZE_MODE_SHRINK();
		case 'fit': return Res.Const.MAP_RESIZE_MODE_FIT();
		case 'vertical-fit': return Res.Const.MAP_RESIZE_MODE_VERTICAL_FIT();
	}
};

export interface TableSaleLocation {
	saleCenterId: number;
	saleLocationName: string;
}

export interface ImageElement {
	name: string;
	value: HTMLImageElement;
}

const GRID_SIZE = 4;

function snapToGrid(quantity: number): number {
	const diff = quantity % GRID_SIZE;
	if (diff <= GRID_SIZE / 2)
		return quantity - diff;
	return quantity + GRID_SIZE - diff;
}

export class Map {

	private selectZone: SelectionZone;

	constructor(
		public width: number,
		public height: number,
		public resizeMode: MapResizeMode,
		public sprites: Sprite[],
		public backgroundImage: ImageElement,
		public backgroundType: 'PREDEFINED' | 'CUSTOM'
	) {
		this.selectZone = new SelectionZone(null, null);
	}

	private drawBackground(ctx: CanvasRenderingContext2D) {
		ctx.save();

		// Las imágenes predefinidas las pintamos "tileadas" como baldosas
		if (this.backgroundType === 'PREDEFINED') {
			const bgPattern = ctx.createPattern(this.backgroundImage.value, 'repeat');
			if (!bgPattern)
				throw Error('Could not create pattern');
			ctx.fillStyle = bgPattern;
			ctx.fillRect(0, 0, this.width, this.height);
		} else {
			// Si es una imagen personalizada, la pintamos rellenando todo el mapa.
			// Asumimos que el usuario habrá puesto la imagen del tamaño del mapa.
			ctx.drawImage(this.backgroundImage.value, 0, 0, this.width, this.height);
		}

		ctx.restore();
	}

	public get SelectionZone(): SelectionZone {
		return this.selectZone;
	}

	public drawOn(canvas: HTMLCanvasElement): void {

		const ctx = canvas.getContext('2d');

		if (ctx !== null) {

			ctx.save();

			const scale = Coordinates.getScale(canvas, { width: this.width, height: this.height });
			ctx.scale(scale.xFactor, scale.yFactor);

			// Es necesario limpiar el canvas cada vez, porque se quedaban restos en cada repintado
			ctx.clearRect(0, 0, this.width, this.height);

			this.drawBackground(ctx);

			// Dibujo primero los muebles, para que las ubicaciones siempre queden por encima
			const furnitures = this.sprites.filter(x => x instanceof Furniture);
			const tables = this.sprites.filter(x => x instanceof Table);

			furnitures.forEach(x => x.drawOn(ctx));
			tables.forEach(x => x.drawOn(ctx));

			this.selectZone.drawOn(ctx);

			ctx.restore();
		}
	}
}

export abstract class Sprite {

	public selected = false;
	public clicked = false;
	private dragging = false;

	private translations: any = {
		'0': (_ctx: CanvasRenderingContext2D, _w: number, _h: number) => { },
		'90': (ctx: CanvasRenderingContext2D, _w: number, h: number) => ctx.translate(0, -h),
		'180': (ctx: CanvasRenderingContext2D, w: number, h: number) => ctx.translate(-w, -h),
		'270': (ctx: CanvasRenderingContext2D, w: number, _h: number) => ctx.translate(-w, 0)
	};

	// Posiciones para trasladar el offset de la posición del ratón
	private offsetX = 0;
	private offsetY = 0;

	constructor(public point: Point, public transformation: Transformation, public image: ImageElement) {
		this.point = point;
		this.image = image;
		this.transformation = transformation;
	}

	public drawOn(ctx: CanvasRenderingContext2D): void {

		ctx.save();

		const deltaX = this.transformation.isVerticallyRotated() ? this.transformation.deltaY : this.transformation.deltaX;
		const deltaY = this.transformation.isVerticallyRotated() ? this.transformation.deltaX : this.transformation.deltaY;

		let x = this.point.x;
		let y = this.point.y;
		let width = this.image.value.width + deltaX;
		let height = this.image.value.height + deltaY;

		if (this.clicked) {
			x += 2;
			y += 2;
			width -= 4;
			height -= 4;
		}

		ctx.translate(x, y);

		ctx.rotate(this.transformation.rotation * Math.PI / 180);

		this.translations[this.transformation.rotation](ctx, width, height);

		if (this.selected) {

			ctx.fillStyle = 'rgba(173, 216, 230, .25)'; // lightblue
			ctx.strokeStyle = 'rgb(19, 51, 62)';

			ctx.fillRect(-3, -3, width + 6, height + 6);
			ctx.strokeRect(-3, -3, width + 6, height + 6);
		}

		ctx.drawImage(this.image.value, 0, 0, width, height);

		ctx.restore();
	}

	public beginDrag(mousePosition: Point): void {

		this.dragging = true;

		this.offsetX = mousePosition.x - this.point.x;
		this.offsetY = mousePosition.y - this.point.y;
	}

	public endDrag(): void {
		this.dragging = false;
		this.offsetX = 0;
		this.offsetY = 0;
	}

	public moveUp() {
		this.move({ x: this.point.x, y: this.point.y - GRID_SIZE });
	}

	public moveDown() {
		this.move({ x: this.point.x, y: this.point.y + GRID_SIZE });
	}

	public moveLeft() {
		this.move({ x: this.point.x - GRID_SIZE, y: this.point.y });
	}

	public moveRight() {
		this.move({ x: this.point.x + GRID_SIZE, y: this.point.y });
	}

	public incrementHeight() {
		this.transformation = this.transformation.increaseY(GRID_SIZE);
	}

	public decrementHeight() {
		// Considero suficiente una imagen de 12 px de alto, que por lo menos la pueda seleccionar
		if (this.transformation.deltaY + (this.transformation.isVerticallyRotated() ? this.image.value.width : this.image.value.height) <= 12)
			return;

		this.transformation = this.transformation.decreaseY(GRID_SIZE);
	}

	public incrementWidth() {
		this.transformation = this.transformation.increaseX(GRID_SIZE);
	}

	public decrementWidth() {
		// Considero suficiente una imagen de 12 px de ancho, que por lo menos la pueda seleccionar
		if (this.transformation.deltaX + (this.transformation.isVerticallyRotated() ? this.image.value.height : this.image.value.width) <= 12)
			return;

		this.transformation = this.transformation.decreaseX(GRID_SIZE);
	}

	public rotate() {
		this.transformation = this.transformation.rotate90Right();
	}

	public moveTo(point: Point): void {

		if (!this.dragging)
			return;

		this.move(point);
	}

	private move(point: Point) {
		const x = Math.max(point.x - this.offsetX, 0);
		const y = Math.max(point.y - this.offsetY, 0);

		this.point = { x: snapToGrid(x), y: snapToGrid(y) };
	}

	public getBounds(): Bounds {

		const isVertical = this.transformation.isVerticallyRotated();

		const deltaX = this.transformation.isVerticallyRotated() ? this.transformation.deltaY : this.transformation.deltaX;
		const deltaY = this.transformation.isVerticallyRotated() ? this.transformation.deltaX : this.transformation.deltaY;
		const width = this.image.value.width + deltaX;
		const height = this.image.value.height + deltaY;

		return new Bounds(this.point.x, this.point.y, isVertical ? height : width, isVertical ? width : height);
	}
}

export class Table extends Sprite {

	public ticketHasBeenPrepared: boolean;
	public ticketIsInactive: boolean;
	public displayName: string;

	constructor(point: Point, transformation: Transformation, image: ImageElement, public location: TableSaleLocation) {
		super(point, transformation, image);
		this.ticketHasBeenPrepared = false;
		this.ticketIsInactive = false;
		this.displayName = '';
	}

	public drawOn(ctx: CanvasRenderingContext2D): void {

		super.drawOn(ctx);

		ctx.save();

		const bounds = this.getBounds();

		ctx.fillStyle = '#000';
		ctx.font = this.selected ? 'bold 12px Segoe UI, Helvetica Neue, Arial' : '12px Segoe UI, Helvetica Neue, Arial';
		ctx.textAlign = 'center';

		const xTextPosition = bounds.x + bounds.width / 2;
		const yTextPosition = bounds.y + bounds.height / 2 + 4;

		const text = this.displayName || this.location.saleLocationName;

		ctx.fillText(text, xTextPosition, yTextPosition);

		const textWidth = ctx.measureText(text).width;


		ctx.font = '18px FontAwesome';
		if (this.ticketHasBeenPrepared) {

			ctx.beginPath();
			ctx.arc(xTextPosition + textWidth + 6, yTextPosition - 5.5, 9, 0, 2 * Math.PI, false);
			ctx.fillStyle = 'white';
			ctx.fill();

			ctx.fillStyle = '#e67e22';
			// fa-check-circle-o
			ctx.fillText('\uf05d', xTextPosition + textWidth + 6, yTextPosition + 1);
		}

		if (this.ticketIsInactive) {

			ctx.beginPath();
			ctx.arc(xTextPosition + textWidth + 6, bounds.y, 9, 0, 2 * Math.PI, false);
			ctx.fillStyle = 'white';
			ctx.fill();

			ctx.fillStyle = '#e74c3c';
			// fa-check-circle-o
			ctx.fillText('\uf017', xTextPosition + textWidth + 6, bounds.y + 6);
		}

		ctx.restore();
	}
}

export class Furniture extends Sprite {
	constructor(point: Point, transformation: Transformation, image: ImageElement) {
		super(point, transformation, image);
	}
}

export class SelectionZone {

	private startPoint: Nullable<Point>;
	private endPoint: Nullable<Point>;

	constructor(startPoint: Nullable<Point>, endPoint: Nullable<Point>) {
		this.startPoint = startPoint;
		this.endPoint = endPoint;
	}

	public setStartPoint(startPoint: Nullable<Point>) {
		this.startPoint = startPoint;
	}

	public setEndPoint(endPoint: Nullable<Point>) {
		this.endPoint = endPoint;
	}

	public drawOn(ctx: CanvasRenderingContext2D): void {

		if (this.startPoint === null || this.endPoint === null)
			return;

		const x = this.getX();
		const y = this.getY();
		const width = this.getWidth();
		const height = this.getHeight();

		ctx.save();

		ctx.fillStyle = 'rgba(173, 216, 230, .25)'; // lightblue
		ctx.strokeStyle = 'rgba(0, 0, 0, .65)';
		ctx.fillRect(x, y, width, height);
		ctx.strokeRect(x, y, width, height);

		ctx.restore();

	}

	public get Bounds(): Bounds {
		return new Bounds(this.getX(), this.getY(), this.getWidth(), this.getHeight());
	}

	private getX(): number {
		if (this.startPoint === null || this.endPoint === null)
			return 0;

		return this.startPoint.x <= this.endPoint.x
			? this.startPoint.x
			: this.endPoint.x;
	}

	private getY(): number {
		if (this.startPoint === null || this.endPoint === null)
			return 0;

		return this.startPoint.y <= this.endPoint.y
			? this.startPoint.y
			: this.endPoint.y;
	}

	private getWidth(): number {
		if (this.startPoint === null || this.endPoint === null)
			return 0;

		return this.startPoint.x <= this.endPoint.x
			? this.endPoint.x - this.startPoint.x
			: this.startPoint.x - this.endPoint.x;
	}

	private getHeight(): number {
		if (this.startPoint === null || this.endPoint === null)
			return 0;

		return this.startPoint.y <= this.endPoint.y
			? this.endPoint.y - this.startPoint.y
			: this.startPoint.y - this.endPoint.y;
	}
}
